// Public merchant signup form. Posts multipart to /api/merchant/signup/.
// On success, stores returned JWT and routes the user to /business/pending.

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { submitMerchantSignup } from '../../services/businessService';
import { useAuth } from '../../contexts/AuthContext';
import Field from '../common/Field';
import '../admin/AdminLayout.css';   // portal-* classes (inputs, buttons, error/success)
import './MerchantSignup.css';

const BUSINESS_TYPES = ['grocery', 'pharmacy', 'restaurant', 'hardware', 'other'];

const DOC_SLOTS = [
  { key: 'business_license', label: 'Business license', hint: 'Registration certificate or similar' },
  { key: 'id_proof', label: 'ID proof', hint: 'Government-issued ID for the contact person' },
  { key: 'tax_document', label: 'Tax document', hint: 'Optional — VAT/GST/EIN certificate' },
  { key: 'other', label: 'Other', hint: 'Optional — anything else that supports your application' },
];

const blank = {
  email: '', password: '',
  contact_name: '', contact_phone: '',
  company_name: '', business_type: 'grocery', address: '',
  description: '', website: '', whatsapp_number: '',
};

export default function MerchantSignup() {
  const [form, setForm] = useState(blank);
  const [docs, setDocs] = useState({});   // slot key → File
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setDoc = (slot) => (e) => setDocs({ ...docs, [slot]: e.target.files?.[0] || null });

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      Object.entries(docs).forEach(([slot, file]) => {
        if (file) payload[`document_${slot}`] = file;
      });
      const res = await submitMerchantSignup(payload);
      // Land the new merchant logged in so the pending page can show
      // their status without a separate login round trip.
      localStorage.setItem('accessToken', res.access_token);
      localStorage.setItem('refreshToken', res.refresh_token);
      localStorage.setItem('userEmail', res.user.email);
      await refresh();
      navigate('/business/pending', { replace: true });
    } catch (err) {
      const data = err?.response?.data;
      if (data?.error === 'email_in_use') setError('That email is already registered. Try signing in instead.');
      else if (data?.error === 'whatsapp_in_use') setError('That WhatsApp number is already linked to another store.');
      else if (data?.error === 'missing_fields') setError(`Missing required fields: ${data.fields.join(', ')}`);
      else if (data?.error === 'password_too_short') setError(`Password needs at least ${data.min} characters.`);
      else if (data?.error === 'doc_too_large') setError(data.detail || 'One of the uploaded documents is too large (3MB max).');
      else setError(data?.detail || err.message || 'Signup failed. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="biz-form-page">
      <div className="biz-form-shell">
        <header>
          <div>
            <h1>Apply as a merchant</h1>
            <p className="biz-form-sub">
              Applications are reviewed by our team within 1–2 business days. You'll be
              able to sign in immediately and track your status.
            </p>
          </div>
          <Link to="/business" className="portal-button secondary">← Back</Link>
        </header>

        {error && <div className="portal-error">{error}</div>}

        <form onSubmit={submit}>
          <section className="biz-section">
            <h3>Account</h3>
            <p className="biz-section-hint">You'll use these to log in to the merchant portal.</p>
            <div className="portal-form-grid">
              <Field label="Email">
                <input type="email" className="portal-search" required value={form.email} onChange={set('email')} />
              </Field>
              <Field label="Password (min 8 characters)">
                <input type="password" className="portal-search" required minLength={8} value={form.password} onChange={set('password')} />
              </Field>
            </div>
          </section>

          <section className="biz-section">
            <h3>Contact person</h3>
            <p className="biz-section-hint">The primary point-of-contact at your business.</p>
            <div className="portal-form-grid">
              <Field label="Full name">
                <input type="text" className="portal-search" required value={form.contact_name} onChange={set('contact_name')} />
              </Field>
              <Field label="Phone (optional)">
                <input type="tel" className="portal-search" value={form.contact_phone} onChange={set('contact_phone')} />
              </Field>
            </div>
          </section>

          <section className="biz-section">
            <h3>Business details</h3>
            <p className="biz-section-hint">Shown to customers in the deals feed once approved.</p>
            <div className="portal-form-grid">
              <Field label="Business name">
                <input type="text" className="portal-search" required value={form.company_name} onChange={set('company_name')} />
              </Field>
              <Field label="Business type">
                <select className="portal-search" value={form.business_type} onChange={set('business_type')}>
                  {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="WhatsApp number">
                <input type="tel" className="portal-search" required value={form.whatsapp_number} onChange={set('whatsapp_number')} placeholder="+1 555 123 4567" />
              </Field>
              <Field label="Website (optional)">
                <input type="url" className="portal-search" value={form.website} onChange={set('website')} placeholder="https://" />
              </Field>
              <Field label="Address" full>
                <input type="text" className="portal-search" value={form.address} onChange={set('address')} />
              </Field>
              <Field label="Short description (optional)" full>
                <textarea className="portal-textarea" value={form.description} onChange={set('description')} placeholder="What kind of products do you sell?" />
              </Field>
            </div>
          </section>

          <section className="biz-section">
            <h3>Verification documents</h3>
            <p className="biz-section-hint">
              Upload supporting documents so our team can verify your business. 3MB max per file —
              JPG, PNG, or PDF. You can submit without documents but approval will be slower.
            </p>
            {DOC_SLOTS.map(slot => (
              <div key={slot.key} className="biz-doc-slot">
                <div className="biz-doc-label">
                  {slot.label}
                  <small>{slot.hint}</small>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--tudu-space-3)' }}>
                  {docs[slot.key] && <span className="biz-doc-attached">attached</span>}
                  <input type="file" accept="image/*,application/pdf" onChange={setDoc(slot.key)} />
                </div>
              </div>
            ))}
          </section>

          <div className="biz-form-actions">
            <Link to="/login">Already have an account? Sign in</Link>
            <button type="submit" className="portal-button" disabled={busy}>
              {busy ? 'Submitting…' : 'Submit application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
